import { useState, useEffect, createContext, useContext } from 'react';

// Create context for translations
const TranslationsContext = createContext();

// Translation provider component
export const TranslationsProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [translations, setTranslations] = useState({});
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch available languages
  const fetchLanguages = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/languages`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch languages');
      }
      
      const languagesData = await response.json();
      setLanguages(languagesData);
      
      return languagesData;
    } catch (err) {
      console.error('Error fetching languages:', err);
      // Fallback to default languages
      const fallbackLanguages = [
        { code: 'en', name: 'English', native_name: 'English', flag: '🇺🇸', direction: 'ltr' },
        { code: 'es', name: 'Spanish', native_name: 'Español', flag: '🇪🇸', direction: 'ltr' },
        { code: 'fr', name: 'French', native_name: 'Français', flag: '🇫🇷', direction: 'ltr' },
        { code: 'de', name: 'German', native_name: 'Deutsch', flag: '🇩🇪', direction: 'ltr' },
        { code: 'it', name: 'Italian', native_name: 'Italiano', flag: '🇮🇹', direction: 'ltr' },
        { code: 'nl', name: 'Dutch', native_name: 'Nederlands', flag: '🇳🇱', direction: 'ltr' },
        { code: 'bs', name: 'Bosnian', native_name: 'Bosanski', flag: '🇧🇦', direction: 'ltr' }
      ];
      setLanguages(fallbackLanguages);
      return fallbackLanguages;
    }
  };

  // Fetch translations for a specific language
  const fetchTranslations = async (languageCode) => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/languages/${languageCode}/translations`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch translations');
      }
      
      const translationsData = await response.json();
      setTranslations(translationsData);
      setError(null);
      
      return translationsData;
    } catch (err) {
      console.error('Error fetching translations:', err);
      setError(err.message);
      
      // Fallback to English if translations fail
      if (languageCode !== 'en') {
        return await fetchTranslations('en');
      }
      
      // Ultimate fallback
      setTranslations({
        header: {
          home: 'Home',
          services: 'Services',
          about: 'About',
          insights: 'Insights',
          affiliates: 'Affiliates',
          contact: 'Contact',
          get_started: 'Get Started'
        }
      });
    } finally {
      setLoading(false);
    }
  };

  // Change language
  const changeLanguage = async (newLanguage) => {
    if (newLanguage === currentLanguage) return;
    
    setCurrentLanguage(newLanguage);
    await fetchTranslations(newLanguage);
    
    // Store preference in localStorage
    localStorage.setItem('preferred_language', newLanguage);
  };

  // Add new language
  const addLanguage = async (languageCode, autoTranslate = true) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/languages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          language_code: languageCode,
          auto_translate: autoTranslate
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to add language');
      }
      
      const result = await response.json();
      
      // Refresh languages list
      await fetchLanguages();
      
      return result;
    } catch (err) {
      console.error('Error adding language:', err);
      throw err;
    }
  };

  // Get translation for a specific key
  const getTranslation = (component, key, fallback = key) => {
    if (!translations[component] || !translations[component][key]) {
      return fallback;
    }
    return translations[component][key];
  };

  // Initialize on mount
  useEffect(() => {
    const initializeTranslations = async () => {
      // Get preferred language from localStorage
      const preferredLanguage = localStorage.getItem('preferred_language') || 'en';
      
      // Fetch available languages
      await fetchLanguages();
      
      // Set current language and fetch translations
      setCurrentLanguage(preferredLanguage);
      await fetchTranslations(preferredLanguage);
    };

    initializeTranslations();
  }, []);

  const value = {
    currentLanguage,
    translations,
    languages,
    loading,
    error,
    changeLanguage,
    addLanguage,
    getTranslation,
    fetchTranslations,
    fetchLanguages
  };

  return (
    <TranslationsContext.Provider value={value}>
      {children}
    </TranslationsContext.Provider>
  );
};

// Hook to use translations
export const useTranslations = () => {
  const context = useContext(TranslationsContext);
  
  if (!context) {
    throw new Error('useTranslations must be used within a TranslationsProvider');
  }
  
  return context;
};

// Hook for component-specific translations
export const useComponentTranslations = (componentName) => {
  const { translations, getTranslation, loading, error } = useTranslations();
  
  const t = (key, fallback = key) => getTranslation(componentName, key, fallback);
  
  const componentTranslations = translations[componentName] || {};
  
  return {
    t,
    translations: componentTranslations,
    loading,
    error
  };
};

export default useTranslations;